import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { POSTHOG_CONFIG, PostHogConfig } from './posthog-config.token';
import { PostHog } from 'posthog-js';

interface PostHogQueueItem {
  event: string;
  props?: Record<string, unknown>;
}
@Injectable({ providedIn: 'root' })
export class PostHogService {
  private posthog: PostHog | null = null;
  private initialized = false;
  private environment: 'server' | 'browser' | null = null;
  private eventQueue: PostHogQueueItem[] = [];
  private initPromise: Promise<void> | null = null;
  private featureFlagsReady: Promise<void> = Promise.resolve();

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Optional() @Inject(POSTHOG_CONFIG) private readonly config: PostHogConfig
  ) {}

  private log(...args: unknown[]): void {
    if (this.config?.debug) {
      console.log(...args);
    }
  }

  async init(): Promise<void> {
    this.log('[PostHog] Init called, initialized:', this.initialized);
    this.log('[PostHog] Config:', this.config);

    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this.initialize();
    return this.initPromise;
  }

  private async initialize(): Promise<void> {
    if (isPlatformServer(this.platformId)) {
      this.log('[PostHog] Running on server - skipping initialization');
      this.environment = 'server';
      this.initialized = true;
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      this.log('[PostHog] Running in browser - starting initialization');
      this.environment = 'browser';
      try {
        this.log('[PostHog] Importing posthog-js module...');
        const module = await import('posthog-js');
        this.log('[PostHog] Module imported successfully:', module);
        this.setupPosthog(module.default);
        await this.waitForFeatureFlagsWithTimeout();
        this.flushQueue();
        this.initialized = true;
        this.log('[PostHog] Initialization complete!');
      } catch (error) {
        console.error('[PostHog] Failed to initialize PostHog:', error);
      }
    }
  }

  private async waitForFeatureFlagsWithTimeout(): Promise<void> {
    const featureFlagsTimeoutMs = 3000;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const timeout = new Promise<void>((resolve) => {
      timeoutId = setTimeout(() => {
        this.log(
          '[PostHog] Timed out waiting for feature flags, continuing anyway'
        );
        resolve();
      }, featureFlagsTimeoutMs);
    });

    try {
      await Promise.race([this.featureFlagsReady, timeout]);
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }
  }

  private setupPosthog(posthogLib: PostHog): void {
    this.log('[PostHog] Setting up PostHog library...');
    this.posthog = posthogLib;

    if (this.config?.apiKey) {
      this.featureFlagsReady = new Promise<void>((resolve) => {
        posthogLib.onFeatureFlags(() => resolve());
      });

      const options: Record<string, unknown> = {
        person_profiles: this.config.personProfiles ?? true,
        autocapture: this.config.autocapture ?? true,
        capture_pageview: this.config.capturePageview ?? true,
        capture_pageleave: this.config.capturePageleave ?? true,
      };

      if (this.config.apiHost) {
        options.api_host = this.config.apiHost;
      }

      if (this.config.disableWebExperiments === false) {
        options.disable_web_experiments = this.config.disableWebExperiments;
      }

      this.log(
        '[PostHog] Initializing with API key:',
        this.config.apiKey?.substring(0, 10) + '...'
      );
      this.log('[PostHog] Options:', options);

      this.posthog.init(this.config.apiKey, options);

      // Expose PostHog to window for debugging
      if (this.config.debug) {
        ((window as unknown) as Record<string, unknown>).posthog = this.posthog;
        this.log('[PostHog] PostHog instance attached to window.posthog');
      }

      const store = this.getStoreFromUrl();
      if (store) {
        this.log('[PostHog] Registering store parameter:', store);
        this.posthog.register({ store });
      }
    } else {
      this.featureFlagsReady = Promise.resolve();
      console.error('[PostHog] No API key provided in config!');
    }
  }

  private getStoreFromUrl(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      const params = new URLSearchParams((window as Window).location.search);
      return params.get('store');
    }
    return null;
  }

  private flushQueue(): void {
    while (this.eventQueue.length > 0) {
      const queueItem = this.eventQueue.shift();
      if (queueItem) {
        const { event, props } = queueItem;
        this.posthog?.capture(event, props);
      }
    }
  }

  isFeatureEnabled(feature: string): boolean {
    if (this.environment === 'browser' && this.posthog) {
      return this.posthog.isFeatureEnabled(feature) || false;
    }
    return false;
  }

  getFeatureFlag(feature: string): boolean {
    if (this.environment === 'browser' && this.posthog) {
      return Boolean(this.posthog.getFeatureFlag(feature) || false);
    }
    return false;
  }

  capture(event: string, props?: Record<string, unknown>): void {
    this.log('[PostHog] Capture called:', event, props);
    this.log(
      '[PostHog] Environment:',
      this.environment,
      'PostHog instance:',
      !!this.posthog
    );

    if (this.environment === 'browser' && this.posthog) {
      this.log('[PostHog] Capturing event...');
      this.posthog.capture(event, props);
    } else {
      this.log('[PostHog] Queueing event (not ready yet)');
      this.eventQueue.push({ event, props });
    }
  }
}
